// src/pages/deliveryNote/DeliveryNoteDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Truck, Package, Printer, ShoppingBag, Store, Calendar, User, FileText } from 'lucide-react';
import { PageHeader, PrintPreviewModal, InfoCard } from '../../components/common';
import { getDeliveryNote } from '../../services/api/offlineSales/deliveryNotes';
import { getOrder } from '../../services/api/offlineSales/orders';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDateTime } from '../../utils/dateUtils';
import { getImageUrl } from '../../utils/imageUtils';
import { exportDeliveryNotePDF } from '../../utils/offlineOrderPdfExporter';
import { getCssVar } from '../../utils/themeColors';
import DeliveryNoteA4Document from './DeliveryNoteA4Document';
import ProductThumb from '../procurement/components/ProductThumb';
import ps from '../procurement/components/ProcurementDetails.module.css';
import styles from './DeliveryNoteDetailsPage.module.css';
import receiptStyles from '../offlineSales/components/OrderReceiptPrintModal.module.css';

const STATUS_COLORS = {
  pending: 'warning',
  delivered: 'success',
  cancelled: 'error',
};

const Badge = ({ type, label }) => (
  <span className={`${styles.badge} ${styles[`badge_${type}`]}`}>{label}</span>
);

const DeliveryNoteDetailsPage = () => {
  const { deliveryNoteId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { error } = useAlert();

  const [deliveryNote, setDeliveryNote] = useState(null);
  const [itemNames, setItemNames] = useState({}); // orderItemId -> { name: "Product name — SKU", image }
  const [loading, setLoading] = useState(true);
  const [showPrint, setShowPrint] = useState(false);

  const loadDeliveryNote = async () => {
    try {
      setLoading(true);
      const result = await getDeliveryNote(deliveryNoteId);
      setDeliveryNote(result);

      if (result?.order?.id) {
        try {
          const order = await getOrder(result.order.id);
          const names = {};
          (order.orderItems || []).forEach(oi => {
            names[oi.id] = {
              name: `${oi.Product?.name || t('common.na')}${oi.ProductVariation?.sku ? ` — ${oi.ProductVariation.sku}` : ''}`,
              image: oi.Product?.image || null,
            };
          });
          setItemNames(names);
        } catch (err) {
          console.error('Failed to load order item details:', err);
        }
      }
    } catch (err) {
      error(err?.response?.data?.message || t('deliveryNote.failedToLoadDeliveryNote'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDeliveryNote(); }, [deliveryNoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrint = () => setShowPrint(true);

  const renderReceipt = () => (
    <div className={receiptStyles.receiptArea}>
      <h2 className={receiptStyles.receiptTitle}>{t('deliveryNote.deliveryNoteNumber')}</h2>
      <div className={receiptStyles.receiptMeta}>
        <div className={receiptStyles.receiptMetaRow}>
          <span>#</span>
          <span>{deliveryNote.deliveryNoteNumber}</span>
        </div>
        <div className={receiptStyles.receiptMetaRow}>
          <span>{t('deliveryNote.orderNumber')}</span>
          <span>#{deliveryNote.order?.orderNumber || t('common.na')}</span>
        </div>
        <div className={receiptStyles.receiptMetaRow}>
          <span>{t('deliveryNote.deliveredAt')}</span>
          <span>{deliveryNote.deliveredAt ? formatDateTime(deliveryNote.deliveredAt) : t('common.na')}</span>
        </div>
      </div>
      <hr className={receiptStyles.receiptDivider} />
      <div className={receiptStyles.receiptItems}>
        {(deliveryNote.items || []).map((item) => (
          <div key={item.id} className={receiptStyles.receiptItem}>
            <span className={receiptStyles.receiptItemName}>
              {itemNames[item.orderItemId]?.name || `${t('deliveryNote.item')} #${item.orderItemId}`}
            </span>
            <span className={receiptStyles.receiptItemQty}>×{item.quantityDelivered}</span>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (!deliveryNote) {
    return <div className={styles.notFound}>{t('errors.notFound')}</div>;
  }

  const renderContent = () => (
    <div className={styles.grid}>
      <div className={styles.column}>
        <InfoCard icon={Truck} title={t('deliveryNote.deliveryNoteNumber')}>
          <div className={ps.infoGrid}>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('common.status')}</span>
              <span className={ps.infoValue}>
                <Badge type={STATUS_COLORS[deliveryNote.status]} label={t(`deliveryNote.status_${deliveryNote.status}`)} />
              </span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('deliveryNote.orderNumber')}</span>
              <span className={ps.infoValue}>
                <ShoppingBag size={14} />
                {deliveryNote.order?.orderNumber ? (
                  <Link to={`/offline-sales/orders/${deliveryNote.order.id}`}>{deliveryNote.order.orderNumber}</Link>
                ) : t('common.na')}
              </span>
            </div>
            {deliveryNote.order?.Shop?.name && (
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('deliveryNote.shop')}</span>
                <span className={ps.infoValue}><Store size={14} />{deliveryNote.order.Shop.name}</span>
              </div>
            )}
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('deliveryNote.deliveredAt')}</span>
              <span className={ps.infoValue}><Calendar size={14} />{deliveryNote.deliveredAt ? formatDateTime(deliveryNote.deliveredAt) : t('common.na')}</span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('deliveryNote.receivedByName')}</span>
              <span className={ps.infoValue}><User size={14} />{deliveryNote.receivedByName || t('common.na')}</span>
            </div>
            {deliveryNote.notes && (
              <div className={`${ps.infoItem} ${ps.fullWidth}`}>
                <span className={ps.infoLabel}>{t('deliveryNote.notes')}</span>
                <p className={ps.notesText}>{deliveryNote.notes}</p>
              </div>
            )}
          </div>
        </InfoCard>

        <InfoCard icon={FileText} title={t('ecomOrders.additionalInformation')}>
          <div className={ps.infoList}>
            <div className={ps.infoListItem}>
              <label>{t('common.created')}</label>
              <p><Calendar size={14} />{formatDateTime(deliveryNote.createdAt)}</p>
            </div>
          </div>
        </InfoCard>
      </div>

      <InfoCard icon={Package} title={t('deliveryNote.itemsDelivered')}>
        <div className={ps.tableWrapper}>
          <table className={ps.itemsTable}>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>#</th>
                <th>{t('deliveryNote.item')}</th>
                <th className={ps.alignRight}>{t('deliveryNote.quantityDelivered')}</th>
              </tr>
            </thead>
            <tbody>
              {(deliveryNote.items || []).map((item, index) => {
                const itemInfo = itemNames[item.orderItemId];
                return (
                  <tr key={item.id}>
                    <td className={ps.itemIndexCell}>{index + 1}</td>
                    <td>
                      <div className={ps.productCellInner}>
                        <ProductThumb src={getImageUrl(itemInfo?.image)} alt={itemInfo?.name || ''} />
                        <div className={ps.productCell}>
                          <span className={ps.productName}>{itemInfo?.name || `${t('deliveryNote.item')} #${item.orderItemId}`}</span>
                        </div>
                      </div>
                    </td>
                    <td className={`${ps.alignRight} ${ps.amountCell} ${ps.amountStrong}`}>{item.quantityDelivered}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </InfoCard>
    </div>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title={deliveryNote.deliveryNoteNumber}
        icon={null}
        breadcrumbs={
          <button onClick={() => navigate('/offline-sales/delivery-notes')} className={styles.backBtn}>
            <ChevronLeft size={16} /> {t('deliveryNote.allDeliveryNotes')}
          </button>
        }
        actions={
          <div className={styles.headerActions}>
            <button className={styles.secondaryBtn} onClick={handlePrint}>
              <Printer size={15} /> {t('common.printReceipt', 'Print Receipt')}
            </button>
          </div>
        }
      />

      {renderContent()}

      {showPrint && (
        <PrintPreviewModal
          title={`Delivery Note — ${deliveryNote.deliveryNoteNumber}`}
          onClose={() => setShowPrint(false)}
          pdfFileName={`Delivery_Note_${deliveryNote.deliveryNoteNumber || deliveryNote.id}.pdf`}
          onDownloadPdf={() => exportDeliveryNotePDF(deliveryNote, itemNames, {
            primary: getCssVar('--color-primary', '#4f46e5'),
            primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
          })}
          renderReceipt={renderReceipt}
          renderA4={() => <DeliveryNoteA4Document deliveryNote={deliveryNote} itemNames={itemNames} />}
        />
      )}
    </div>
  );
};

export default DeliveryNoteDetailsPage;
